import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Container, Typography, Card, CardContent, 
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, alpha, Grid, Avatar
} from '@mui/material';
import { 
  Android, Security, Code, ExpandMore, ArrowForward, 
  Smartphone, Storage, AutoAwesome, GppGood, Bolt, Search, HelpOutline 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Architecture & UI",
    question: "Why choose native Android development over cross-platform frameworks like Flutter or React Native?",
    answer: "Native Android using Kotlin and Jetpack Compose delivers zero bridge overhead, direct low-level hardware API access (CameraX, Bluetooth, Sensors), reduced memory footprint, and guaranteed 60 FPS rendering for complex UI animations."
  },
  {
    category: "Architecture & UI",
    question: "Why is Jetpack Compose preferred over traditional XML layouts?",
    answer: "Jetpack Compose is Google's modern declarative UI toolkit that eliminates layout rendering bottlenecks, reduces boilerplate code by up to 40%, simplifies dynamic state management, and speeds up UI iteration cycles."
  },
  {
    category: "Architecture & UI",
    question: "What architectural pattern do you follow for Android applications?",
    answer: "Applications are built following Google's official Clean Architecture guidelines paired with MVVM or MVI patterns. This separates UI, domain business logic, and data layers, making code maintainable, scalable, and unit-testable."
  },
  {
    category: "Security & Store Compliance",
    question: "Do you handle complete Google Play Store publishing and compliance?",
    answer: "Yes. End-to-end publishing lifecycle governance is included: Android App Bundle (.aab) generation, target SDK level compliance, R8 code shrinking/obfuscation, Data Safety declarations, and Google Play Console setup."
  },
  {
    category: "Security & Store Compliance",
    question: "How do you protect the app against reverse engineering and security threats?",
    answer: "Apps are secured using R8/ProGuard code obfuscation, SSL Certificate Pinning to prevent MITM attacks, EncryptedSharedPreferences for token storage, root detection, and biometric authentication APIs."
  },
  {
    category: "Performance & Offline",
    question: "How do you handle offline functionality and data synchronization?",
    answer: "An offline-first architecture is implemented using encrypted local databases (Room DB / SQLCipher) combined with WorkManager for background synchronization and Retrofit/Ktor for reliable RESTful communication when connectivity returns."
  },
  {
    category: "Performance & Offline",
    question: "How do you optimize app download size for users with limited bandwidth?",
    answer: "App sizes are minimized by leveraging Android App Bundles (AAB) for dynamic delivery, vector drawables, WebP image formats, resource shrinking, and unused code elimination during compilation."
  },
  {
    category: "Testing & Quality",
    question: "What is your testing strategy to ensure app stability?",
    answer: "A multi-layered testing strategy is applied: Unit tests (JUnit 5, Mockk) for ViewModel and domain logic, integration tests for repository layers, and UI/instrumentation tests (Espresso) targeting an 80%+ test coverage threshold."
  },
  {
    category: "Testing & Quality",
    question: "How do you monitor app crashes and performance metrics post-launch?",
    answer: "Real-time production monitoring is configured using Firebase Crashlytics and Performance Monitoring to detect runtime exceptions, memory leaks, and network latency issues before users report them."
  }
];

const AndroidDevelopment = () => {
  const navigate = useNavigate();

  const siteUrl = "https://vikashtiwaridev.web.app/services/android-development";
  const ogImageUrl = "https://vikashtiwaridev.web.app/og-android-dev.png";
  const pageTitle = "Native Android App Development Services (Kotlin & Compose) | Vikash Tiwari";
  const pageDescription = "High-performance native Android application development using Kotlin, Jetpack Compose, and Clean Architecture. Scalable, secure, offline-first, and Play Store ready.";

  // Dynamic Structured Data (Schema Markup)
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Native Android Development Services",
      "serviceType": "Mobile Application Development",
      "provider": {
        "@type": "Person",
        "name": "Vikash Tiwari",
        "url": "https://vikashtiwaridev.web.app/",
        "jobTitle": "Senior Mobile Application Engineer"
      },
      "areaServed": "Worldwide",
      "description": pageDescription,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    }
  ];

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Architecture & UI', 'Security & Store Compliance', 'Performance & Offline', 'Testing & Quality'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const capabilities = [
    {
      title: "Kotlin & Jetpack Compose",
      desc: "Declarative, reactive UI engineering built for 60fps animations, robust state management, and zero legacy XML rendering bottlenecks.",
      icon: <Smartphone sx={{ fontSize: 28, color: '#3B82F6' }} />,
      gradient: "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.05) 100%)"
    },
    {
      title: "Clean Architecture & Coroutines",
      desc: "Maintainable MVVM/MVI architectures paired with Kotlin Flow for asynchronous, non-blocking thread management and strict testability.",
      icon: <Code sx={{ fontSize: 28, color: '#8B5CF6' }} />,
      gradient: "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(109, 40, 217, 0.05) 100%)"
    },
    {
      title: "Offline-First & Room DB",
      desc: "Real-time sync, encrypted local storage via SQLCipher, background execution with WorkManager, and resilient REST/GraphQL layers.",
      icon: <Storage sx={{ fontSize: 28, color: '#EC4899' }} />,
      gradient: "linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(190, 24, 93, 0.05) 100%)"
    },
    {
      title: "Play Store & Security Governance",
      desc: "R8 code obfuscation, SSL pinning, automated CI/CD deployment pipelines, memory leak audits, and 100% Google Play compliance.",
      icon: <Security sx={{ fontSize: 28, color: '#10B981' }} />,
      gradient: "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(4, 120, 87, 0.05) 100%)"
    }
  ];

  const metrics = [
    { label: "Target Crash-Free Rate", value: "99.9%", icon: <GppGood sx={{ fontSize: 20 }} /> },
    { label: "UI Frame Rate", value: "60 FPS", icon: <Bolt sx={{ fontSize: 20 }} /> },
    { label: "Architectural Standard", value: "MVVM / MVI", icon: <Code sx={{ fontSize: 20 }} /> },
    { label: "Test Coverage Target", value: "80%+", icon: <AutoAwesome sx={{ fontSize: 20 }} /> }
  ];

  const techCategories = [
    { name: "Language & UI", items: ["Kotlin", "Jetpack Compose", "Material 3", "Coroutines & Flow"] },
    { name: "Architecture & DI", items: ["Clean Architecture", "MVVM / MVI", "Hilt / Dagger", "Room DB"] },
    { name: "Networking & Cloud", items: ["Retrofit", "Ktor", "Firebase FCM", "WorkManager"] },
    { name: "Quality & Security", items: ["JUnit 5", "Mockk", "Espresso", "R8 / ProGuard", "SSL Pinning"] }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="Native Android Developer, Kotlin App Development, Jetpack Compose Expert, Android Engineer, Clean Architecture Android" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={siteUrl} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            pt: { xs: 10, md: 16 },
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.08) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }} 
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>

              <Chip
                icon={<Android sx={{ fontSize: '18px !important', color: '#3B82F6' }} />}
                label="Production-Grade Native Engineering"
                sx={{
                  mb: 3,
                  py: 2,
                  px: 1,
                  bgcolor: alpha('#3B82F6', 0.08),
                  color: '#60A5FA',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Build High-Scale Apps With{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  Native Android
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: '#94A3B8',
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                Modern, resilient mobile applications built with Kotlin, Jetpack Compose, and Clean Architecture. Purpose-built for high execution speed, hardware integration, and Play Store readiness.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 8 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    py: 1.8,
                    px: 4,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                    boxShadow: '0px 8px 24px rgba(37, 99, 235, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1D4ED8 0%, #6D28D9 100%)',
                      boxShadow: '0px 12px 32px rgba(37, 99, 235, 0.5)',
                    }
                  }}
                >
                  Start Architecture Review
                </Button>

                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#3B82F6',
                      bgcolor: alpha('#3B82F6', 0.05)
                    }
                  }}
                >
                  Technical FAQs
                </Button>
              </Stack>

              {/* Metrics Banner */}
              <Grid container spacing={2}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        bgcolor: 'rgba(15, 23, 42, 0.6)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Box sx={{ display: 'inline-flex', p: 1, borderRadius: '10px', bgcolor: alpha('#3B82F6', 0.15), color: '#60A5FA', mb: 1 }}>
                        {m.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          background: 'linear-gradient(90deg, #60A5FA, #A78BFA)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>

        {/* ================= CAPABILITIES SECTION (Strict 2x2 Grid with Correct Padding) ================= */}
        <Container component="section" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                mb: 2,
                letterSpacing: '-0.01em',
                color: '#F8FAFC'
              }}
            >
              Engineered for High-Consequence Workloads
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Strict adherence to official Android guidelines, modern reactive UI paradigms, and robust architectural principles.
            </Typography>
          </Box>

          {/* 2x2 CSS Grid Layout */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              rowGap: { xs: 3, md: 4 },
              columnGap: { xs: 3, md: 4 }
            }}
          >
            {capabilities.map((cap, i) => (
              <Card
                key={i}
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: '20px',
                  bgcolor: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 3, // Single source of padding on parent card
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Avatar
                      sx={{
                        background: cap.gradient,
                        width: 56,
                        height: 56,
                        borderRadius: '14px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      {cap.icon}
                    </Avatar>

                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#64748B' }}>
                      0{i + 1}
                    </Typography>
                  </Box>

                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {cap.title}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {cap.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= TECH STACK MATRIX SECTION ================= */}
        <Box
          component="section"
          sx={{
            py: { xs: 8, md: 10 },
            bgcolor: 'rgba(15, 23, 42, 0.7)',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip
                label="Technical Foundation"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  bgcolor: alpha('#3B82F6', 0.1),
                  color: '#60A5FA',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}
              />
              <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                Production Android Toolchain
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                A modern, battle-tested toolchain selected for zero runtime bloat, fast compilation, and long-term maintainability.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {techCategories.map((cat, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: '16px',
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#3B82F6', mb: 2 }}>
                      {cat.name}
                    </Typography>
                    <Stack spacing={1}>
                      {cat.items.map((item, itemIdx) => (
                        <Box 
                          key={itemIdx} 
                          sx={{ 
                            py: 0.8, 
                            px: 1.5, 
                            borderRadius: '8px', 
                            bgcolor: 'rgba(255, 255, 255, 0.03)', 
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#E2E8F0'
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ================= TECHNICAL FAQ SECTION ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#8B5CF6' }} />} 
              label="Technical FAQs" 
              sx={{ mb: 2, bgcolor: alpha('#8B5CF6', 0.1), color: '#A78BFA', fontWeight: 600 }} 
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Clear architectural answers regarding Kotlin, Jetpack Compose, offline sync, security, and Play Store publishing.
            </Typography>
          </Box>

          {/* Search Bar & Category Filter */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search Android FAQs (e.g., Compose, Coroutines, Room DB, Security)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#3B82F6' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#3B82F6' },
                  '&.Mui-focused fieldset': { borderColor: '#8B5CF6' }
                }
              }}
            />

            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#3B82F6', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': { 
                  color: '#94A3B8', 
                  fontWeight: 600, 
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#60A5FA' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#3B82F6' }} />}
                      sx={{ p: 2.5, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip 
                        label={faq.category} 
                        size="small" 
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try searching for alternative topics or select "All".</Typography>
              </Box>
            )}
          </Box>

        </Container>

        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Ready to Build Your Native Android Application?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Schedule a technical scoping session to analyze your requirements, evaluate Kotlin Clean Architecture, and define project timelines.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Get Started
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default AndroidDevelopment;